import { useState } from 'react';
import { Section } from '~/components/layout';
import { ButtonLink } from '~/components/ui';
import type { PayrollSavingsCalculatorContent } from '~/content/types';
import { cn } from '~/lib/cn';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const MAX_EMPLOYEES = 500;
const MAX_FEE = 10_000;
const DEFAULT_EMPLOYEES = 25;
const DEFAULT_FEE = '10';
const DEFAULT_SUBSCRIPTION = '25';

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const formatUsd = (value: number) =>
  value >= 1_000_000 ? compactUsd.format(value) : usd.format(value);

function parseAmount(input: string) {
  const parsed = Number.parseFloat(input);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function sanitizeAmountInput(value: string): string | null {
  if (value === '') return value;
  if (!/^\d*\.?\d*$/.test(value)) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed < 0 || parsed > MAX_FEE ? String(clampNumber(parsed, 0, MAX_FEE)) : value;
}

function amountUpdater(setValue: (value: string) => void) {
  return (value: string) => {
    const next = sanitizeAmountInput(value);
    if (next !== null) setValue(next);
  };
}

/** Compares nsave's $0 payroll fees with the visitor's current transfer fee and subscription. */
export function PayrollSavingsCalculator({
  content,
}: {
  content: PayrollSavingsCalculatorContent;
}) {
  const [employees, setEmployees] = useState(DEFAULT_EMPLOYEES);
  const [feeInput, setFeeInput] = useState(DEFAULT_FEE);
  const [subscriptionInput, setSubscriptionInput] = useState(DEFAULT_SUBSCRIPTION);

  const yearlyTransferFees = employees * parseAmount(feeInput) * 12;
  const yearlySubscriptions = employees * parseAmount(subscriptionInput) * 12;
  const yearlySavings = yearlyTransferFees + yearlySubscriptions;
  const sliderProgress = ((employees - 1) / (MAX_EMPLOYEES - 1)) * 100;

  return (
    <Section className="!pt-0">
      <div className="rounded-[28px] bg-grey-50 px-5 py-10 md:px-10 md:py-14 lg:px-14">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-h3 leading-tight font-semibold tracking-[-0.03em] text-balance md:text-h2">
            {content.title}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-body text-grey-700">{content.description}</p>
        </div>

        <div className="mx-auto mt-10 grid max-w-5xl gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <div className="rounded-[22px] bg-white p-6 md:p-8">
            <p className="text-body-s text-grey-700">{content.presetsLabel}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {content.presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  aria-pressed={employees === preset.employees}
                  onClick={() => setEmployees(preset.employees)}
                  className={cn(
                    'rounded-pill px-4 py-2 text-body-s font-medium transition-colors',
                    employees === preset.employees
                      ? 'bg-ink text-white'
                      : 'bg-grey-50 text-ink hover:bg-grey-100',
                  )}>
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="mt-8 flex items-end justify-between gap-4">
              <label htmlFor="payroll-employees" className="text-body-s text-grey-700">
                {content.employeesLabel}
              </label>
              <span className="text-h3 leading-none font-semibold tracking-[-0.03em] tabular-nums">
                {employees}
              </span>
            </div>
            <input
              id="payroll-employees"
              type="range"
              min={1}
              max={MAX_EMPLOYEES}
              step={1}
              value={employees}
              onChange={(event) =>
                setEmployees(clampNumber(Number(event.target.value) || 1, 1, MAX_EMPLOYEES))
              }
              style={{
                background: `linear-gradient(to right, var(--color-red-500) ${sliderProgress}%, var(--color-grey-100) ${sliderProgress}%)`,
              }}
              className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full accent-red-500 rtl:[direction:ltr]"
            />
            <div className="mt-2 flex justify-between text-body-s text-grey-600">
              <span>1</span>
              <span>{MAX_EMPLOYEES}+</span>
            </div>

            <div className="mt-8 grid gap-6 border-t border-black-6 pt-7 sm:grid-cols-2">
              <div className="min-w-0">
                <p className="text-body-s font-semibold text-red-500">{content.nsaveColumnLabel}</p>
                <FixedAmount label={content.feeLabel} />
                <FixedAmount label={content.subscriptionLabel} />
                <p className="mt-4 text-body-s text-grey-600">{content.nsaveNote}</p>
              </div>
              <div className="min-w-0">
                <p className="text-body-s font-semibold text-ink">{content.providerColumnLabel}</p>
                <AmountInput
                  id="payroll-fee"
                  label={content.feeLabel}
                  value={feeInput}
                  onChange={amountUpdater(setFeeInput)}
                />
                <AmountInput
                  id="payroll-subscription"
                  label={content.subscriptionLabel}
                  value={subscriptionInput}
                  onChange={amountUpdater(setSubscriptionInput)}
                />
                <p className="mt-4 text-body-s text-grey-600">{content.providerNote}</p>
              </div>
            </div>
          </div>

          <div aria-live="polite" className="flex flex-col rounded-[22px] bg-white p-6 md:p-8">
            <p className="text-body-s text-grey-700">{content.savingsLabel}</p>
            <strong className="mt-3 block min-w-0 text-[clamp(3rem,6vw,4.5rem)] leading-none font-semibold tracking-[-0.05em] text-red-500 tabular-nums">
              {formatUsd(yearlySavings)}
            </strong>
            <p className="mt-3 text-body-s text-grey-700">
              {formatUsd(yearlySavings / 12)} {content.monthlySavingsSuffix}
            </p>

            <div className="mt-7 border-t border-black-6 pt-6">
              <p className="text-body-s text-grey-700">{content.breakdownLabel}</p>
              <dl className="mt-5 space-y-4 text-body">
                <BreakdownRow label={content.transferFeesLabel} value={yearlyTransferFees} />
                <BreakdownRow label={content.subscriptionsLabel} value={yearlySubscriptions} />
                <div className="flex justify-between gap-4 border-t border-black-6 pt-4 font-semibold">
                  <dt>{content.totalLabel}</dt>
                  <dd className="text-red-500 tabular-nums">{formatUsd(yearlySavings)}</dd>
                </div>
              </dl>
            </div>

            <div className="mt-8 grid gap-3">
              <ButtonLink
                href={content.primaryCta.href}
                external={content.primaryCta.external}
                size="md"
                className="w-full">
                {content.primaryCta.label}
              </ButtonLink>
              <ButtonLink
                href={content.secondaryCta.href}
                external={content.secondaryCta.external}
                variant="outline"
                size="md"
                className="w-full">
                {content.secondaryCta.label}
              </ButtonLink>
            </div>
            <p className="mt-6 text-[12px] leading-relaxed text-grey-600">{content.disclaimer}</p>
          </div>
        </div>
      </div>
    </Section>
  );
}

function FixedAmount({ label }: { label: string }) {
  return (
    <div className="mt-4">
      <p className="text-body-s text-grey-700">{label}</p>
      <p className="mt-2 flex h-12 items-center rounded-[14px] bg-red-500/10 px-4 text-body font-semibold text-red-500">
        $ 0
      </p>
    </div>
  );
}

function AmountInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="mt-4">
      <label htmlFor={id} className="block text-body-s text-grey-700">
        {label}
      </label>
      <span className="mt-2 flex h-12 items-center gap-2 rounded-[14px] border border-black-6 px-4 transition-shadow focus-within:border-ink focus-within:ring-2 focus-within:ring-red-500/10">
        <span className="text-body text-grey-500">$</span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full min-w-0 bg-transparent text-body text-ink outline-none"
        />
      </span>
    </div>
  );
}

function BreakdownRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-grey-900">{label}</dt>
      <dd className="font-semibold tabular-nums">{formatUsd(value)}</dd>
    </div>
  );
}
